import { useScreenWidth } from "@msys/ui";
import {
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Button as MuiButton,
  buttonClasses,
  ButtonProps as MuiButtonProps,
  IconButton,
} from "@mui/material";
import React from "react";

interface ButtonCreateProps extends MuiButtonProps {
  title: string;
  compact?: true;
  Icon?: React.ReactElement;
  mobileVariant?: "icon" | "filled";
}

export const ButtonCreate = React.forwardRef<
  HTMLButtonElement,
  ButtonCreateProps
>(
  (
    {
      title,
      variant = "contained",
      size = "small",
      color = "primary",
      children,
      compact,
      Icon,
      mobileVariant = "icon",
      ...props
    },
    ref
  ) => {
    const { isMaxPhone } = useScreenWidth();
    if (isMaxPhone || compact) {
      if (mobileVariant === "filled") {
        return (
          <MuiButton
            ref={ref}
            variant="contained"
            color={color}
            size={size}
            aria-label={title}
            {...props}
          >
            {Icon ?? <AddIcon />}
          </MuiButton>
        );
      }
      return (
        <IconButton
          ref={ref}
          size="small"
          color={color}
          aria-label={title}
          {...props}
        >
          {Icon ?? <AddIcon />}
        </IconButton>
      );
    }
    return (
      <MuiButton
        ref={ref}
        style={{ whiteSpace: "nowrap" }}
        startIcon={Icon ?? <AddIcon />}
        size={size}
        variant={variant}
        color={color}
        {...props}
      >
        {title}
      </MuiButton>
    );
  }
);

interface ButtonIconWithArrowProps extends MuiButtonProps {
  Icon: React.ReactElement;
  open: boolean;
}

export const ButtonIconWithArrow = React.forwardRef<
  HTMLButtonElement,
  ButtonIconWithArrowProps
>(({ Icon, open, size = "small", variant = "text", ...props }, ref) => {
  return (
    <MuiButton
      ref={ref}
      style={{ whiteSpace: "nowrap" }}
      endIcon={
        <KeyboardArrowDown
          fontSize="small"
          sx={{
            transition: "transform 0.2s ease-out",
            ...(open ? { transform: `rotate(-180deg)` } : undefined),
          }}
        />
      }
      size={size}
      variant={variant}
      sx={{ minWidth: "auto", [`.${buttonClasses.endIcon}`]: { ml: 0 } }}
      {...props}
    >
      {Icon}
    </MuiButton>
  );
});
